import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { api } from '../context/AppContext';

/**
 * AUTH DEBUG PAGE
 * This page helps diagnose OAuth authentication issues
 * URL: /auth-debug
 */
const AuthDebug = () => {
  const location = useLocation();
  const [debugInfo, setDebugInfo] = useState({});
  const [apiTestResult, setApiTestResult] = useState(null);
  const [exchangeResult, setExchangeResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialUrl, setInitialUrl] = useState(null);
  const capturedInitialUrl = useRef(false);

  useEffect(() => {
    // CRITICAL: Capture the VERY FIRST URL before any React routing changes it
    if (!capturedInitialUrl.current) {
      capturedInitialUrl.current = true;
      
      // First, check if we have a pre-React captured URL (most reliable)
      const preReactUrl = sessionStorage.getItem('_auth_initial_url');
      if (preReactUrl) {
        try {
          const parsed = JSON.parse(preReactUrl);
          setInitialUrl({
            ...parsed,
            source: 'pre-react'
          });
        } catch (e) {
        }
      }
      
      // Also capture current URL
      const firstUrl = {
        fullUrl: window.location.href,
        hash: window.location.hash,
        search: window.location.search,
        pathname: window.location.pathname,
        timestamp: new Date().toISOString(),
        source: 'react-mount'
      };
      
      // If no pre-react URL, use the current one
      if (!preReactUrl) {
        setInitialUrl(firstUrl);
      }
      
      
      // Also save to sessionStorage in case page refreshes
      sessionStorage.setItem('auth_debug_initial_url', JSON.stringify(firstUrl));
    }
    
    // Capture all URL information
    const info = {
      timestamp: new Date().toISOString(),
      fullUrl: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      origin: window.location.origin,
      
      // Parse query params
      queryParams: Object.fromEntries(new URLSearchParams(window.location.search)),
      
      // Parse hash params  
      hashParams: Object.fromEntries(new URLSearchParams(window.location.hash.replace('#', ''))),
      
      // React Router location
      reactLocation: {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      
      // LocalStorage
      localStorage: {
        session_token: localStorage.getItem('session_token') ? 
          localStorage.getItem('session_token').substring(0, 20) + '...' : null,
        hasToken: !!localStorage.getItem('session_token'),
      },
      
      // Environment
      backendUrl: process.env.REACT_APP_BACKEND_URL,
      
      // Check for saved initial URL
      savedInitialUrl: sessionStorage.getItem('auth_debug_initial_url'),
    };
    
    setDebugInfo(info);
  }, [location]);

  // Test API with current token
  const testApi = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      if (!token) {
        setApiTestResult({ error: 'No token in localStorage' });
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: { 'X-Session-Token': token }
      });
      
      const data = await response.json();
      setApiTestResult({
        status: response.status,
        ok: response.ok,
        data: data
      });
    } catch (error) {
      setApiTestResult({ error: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Try to exchange session_id manually
  const tryExchange = async () => {
    setLoading(true);
    try {
      // Get session_id from URL (try both query and hash)
      // Also try from initial URL if current URL doesn't have it
      const queryParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
      
      let sessionId = queryParams.get('session_id') || hashParams.get('session_id');
      
      // If not found, try from initial URL
      if (!sessionId && initialUrl) {
        const initialHashParams = new URLSearchParams(initialUrl.hash?.replace('#', '') || '');
        const initialQueryParams = new URLSearchParams(initialUrl.search?.replace('?', '') || '');
        sessionId = initialHashParams.get('session_id') || initialQueryParams.get('session_id');
        if (sessionId) {
        }
      }
      
      // Also try from saved initial URL in sessionStorage
      if (!sessionId) {
        const savedUrl = sessionStorage.getItem('auth_debug_initial_url');
        if (savedUrl) {
          try {
            const parsed = JSON.parse(savedUrl);
            const savedHashParams = new URLSearchParams(parsed.hash?.replace('#', '') || '');
            const savedQueryParams = new URLSearchParams(parsed.search?.replace('?', '') || '');
            sessionId = savedHashParams.get('session_id') || savedQueryParams.get('session_id');
            if (sessionId) {
            }
          } catch (e) {}
        }
      }
      
      if (!sessionId) {
        setExchangeResult({ error: 'No session_id found in URL (current, initial, or saved)' });
        return;
      }
      
      
      const response = await api.post('/auth/session', { session_id: sessionId });
      
      // Store token
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      
      setExchangeResult({
        success: true,
        user: response.data.user,
        tokenStored: !!localStorage.getItem('session_token')
      });
      
      // Refresh debug info
      setDebugInfo(prev => ({
        ...prev,
        localStorage: {
          session_token: localStorage.getItem('session_token')?.substring(0, 20) + '...',
          hasToken: !!localStorage.getItem('session_token'),
        }
      }));
      
    } catch (error) {
      console.error('[AuthDebug] Exchange error:', error);
      setExchangeResult({ 
        error: error.message,
        response: error.response?.data 
      });
    } finally {
      setLoading(false);
    }
  };

  // Clear localStorage
  const clearStorage = () => {
    localStorage.clear();
    sessionStorage.removeItem('auth_debug_initial_url');
    setDebugInfo(prev => ({
      ...prev,
      localStorage: { session_token: null, hasToken: false }
    }));
    setApiTestResult(null);
    setExchangeResult(null);
    setInitialUrl(null);
    capturedInitialUrl.current = false;
  };

  // Go to dashboard
  const goToDashboard = () => {
    window.location.href = '/dashboard';
  };

  // Start fresh login
  const startLogin = () => {
    // Use the SAME redirect URL as the normal login flow
    const redirectUrl = window.location.origin + '/auth/callback';
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.href = authUrl;
  };
  
  // Start login but return to debug page (for comparison)
  const startLoginToDebug = () => {
    const redirectUrl = window.location.origin + '/auth-debug';
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.href = authUrl;
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-2 text-orange-500">🔍 Auth Debug Page</h1>
        <p className="text-gray-400 mb-6">Cette page aide à diagnostiquer les problèmes d'authentification</p>

        {/* URL Info */}
        <div className="bg-gray-800 rounded-lg p-4 mb-4">
          <h2 className="text-xl font-semibold mb-3 text-green-400">📍 URL Information</h2>
          <div className="space-y-2 font-mono text-sm">
            <p><span className="text-gray-400">Full URL:</span> <span className="text-yellow-300 break-all">{debugInfo.fullUrl}</span></p>
            <p><span className="text-gray-400">Pathname:</span> {debugInfo.pathname}</p>
            <p><span className="text-gray-400">Search (?):</span> <span className="text-cyan-300">{debugInfo.search || '(empty)'}</span></p>
            <p><span className="text-gray-400">Hash (#):</span> <span className="text-cyan-300">{debugInfo.hash || '(empty)'}</span></p>
          </div>
        </div>

        {/* INITIAL URL - Most important for debugging */}
        {initialUrl && (
          <div className="bg-purple-900/50 border border-purple-500 rounded-lg p-4 mb-4">
            <h2 className="text-xl font-semibold mb-3 text-purple-400">
              🎯 URL INITIALE 
              <span className="text-sm font-normal ml-2">
                (source: {initialUrl.source || 'unknown'})
              </span>
            </h2>
            <div className="space-y-2 font-mono text-sm">
              <p><span className="text-gray-400">Full URL:</span> <span className="text-yellow-300 break-all">{initialUrl.fullUrl}</span></p>
              <p><span className="text-gray-400">Hash (#):</span> <span className="text-cyan-300">{initialUrl.hash || '(empty)'}</span></p>
              <p><span className="text-gray-400">Search (?):</span> <span className="text-cyan-300">{initialUrl.search || '(empty)'}</span></p>
              <p><span className="text-gray-400">Capturé à:</span> {initialUrl.timestamp}</p>
            </div>
            {/* Check if session_id was in initial URL */}
            {(() => {
              const hashParams = new URLSearchParams(initialUrl.hash?.replace('#', '') || '');
              const queryParams = new URLSearchParams(initialUrl.search?.replace('?', '') || '');
              const sessionId = hashParams.get('session_id') || queryParams.get('session_id');
              return sessionId ? (
                <div className="mt-3 p-2 bg-green-900/50 rounded">
                  <p className="text-green-400 font-bold">✅ session_id TROUVÉ dans l'URL initiale!</p>
                  <p className="text-sm text-gray-300">ID: {sessionId.substring(0, 30)}...</p>
                </div>
              ) : (
                <div className="mt-3 p-2 bg-red-900/50 rounded">
                  <p className="text-red-400 font-bold">❌ Pas de session_id dans l'URL initiale</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Cela signifie que Emergent Auth n'a pas ajouté le session_id à l'URL de retour.
                  </p>
                </div>
              );
            })()}
          </div>
        )}
        
        {/* Pre-React URL (if different) */}
        {(() => {
          const preReactUrl = sessionStorage.getItem('_auth_initial_url');
          if (preReactUrl && initialUrl?.source !== 'pre-react') {
            try {
              const parsed = JSON.parse(preReactUrl);
              const hashParams = new URLSearchParams(parsed.hash?.replace('#', '') || '');
              const sessionId = hashParams.get('session_id');
              return (
                <div className="bg-blue-900/50 border border-blue-500 rounded-lg p-4 mb-4">
                  <h2 className="text-xl font-semibold mb-3 text-blue-400">
                    🔍 URL PRE-REACT (avant chargement React)
                  </h2>
                  <div className="space-y-2 font-mono text-sm">
                    <p><span className="text-gray-400">Full URL:</span> <span className="text-yellow-300 break-all">{parsed.fullUrl}</span></p>
                    <p><span className="text-gray-400">Hash (#):</span> <span className="text-cyan-300">{parsed.hash || '(empty)'}</span></p>
                  </div>
                  {sessionId && (
                    <div className="mt-3 p-2 bg-green-900/50 rounded">
                      <p className="text-green-400 font-bold">✅ session_id trouvé: {sessionId.substring(0, 20)}...</p>
                    </div>
                  )}
                </div>
              );
            } catch (e) { return null; }
          }
          return null;
        })()}

        {/* Parsed Params */}
        <div className="bg-gray-800 rounded-lg p-4 mb-4">
          <h2 className="text-xl font-semibold mb-3 text-green-400">🔑 Parsed Parameters</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="font-medium text-yellow-400 mb-2">Query Params (?)</h3>
              <pre className="bg-gray-900 p-2 rounded text-xs overflow-auto">
                {JSON.stringify(debugInfo.queryParams, null, 2) || '{}'}
              </pre>
            </div>
            <div>
              <h3 className="font-medium text-yellow-400 mb-2">Hash Params (#)</h3>
              <pre className="bg-gray-900 p-2 rounded text-xs overflow-auto">
                {JSON.stringify(debugInfo.hashParams, null, 2) || '{}'}
              </pre>
            </div>
          </div>
          
          {/* Session ID Detection */}
          <div className="mt-4 p-3 rounded bg-gray-900">
            <p className="font-medium">
              session_id trouvé: {' '}
              {debugInfo.queryParams?.session_id || debugInfo.hashParams?.session_id ? (
                <span className="text-green-400">✅ OUI</span>
              ) : (
                <span className="text-red-400">❌ NON</span>
              )}
            </p>
            {(debugInfo.queryParams?.session_id || debugInfo.hashParams?.session_id) && (
              <p className="text-sm text-gray-400 mt-1">
                Source: {debugInfo.queryParams?.session_id ? 'Query params (?)' : 'Hash params (#)'}
              </p>
            )}
          </div>
        </div>

        {/* LocalStorage */}
        <div className="bg-gray-800 rounded-lg p-4 mb-4">
          <h2 className="text-xl font-semibold mb-3 text-green-400">💾 LocalStorage</h2>
          <div className="space-y-2">
            <p>
              <span className="text-gray-400">session_token:</span>{' '}
              {debugInfo.localStorage?.hasToken ? (
                <span className="text-green-400">✅ {debugInfo.localStorage.session_token}</span>
              ) : (
                <span className="text-red-400">❌ Absent</span>
              )}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-gray-800 rounded-lg p-4 mb-4">
          <h2 className="text-xl font-semibold mb-3 text-green-400">🎯 Actions</h2>
          <div className="flex flex-wrap gap-3">
            <Button onClick={startLogin} className="bg-orange-500 hover:bg-orange-600">
              🔐 Login → /auth/callback
            </Button>
            <Button onClick={startLoginToDebug} className="bg-orange-700 hover:bg-orange-800">
              🔐 Login → /auth-debug
            </Button>
            <Button onClick={tryExchange} disabled={loading} className="bg-blue-500 hover:bg-blue-600">
              {loading ? '⏳' : '🔄'} Échanger session_id
            </Button>
            <Button onClick={testApi} disabled={loading} className="bg-green-500 hover:bg-green-600">
              {loading ? '⏳' : '🧪'} Tester API /auth/me
            </Button>
            <Button onClick={goToDashboard} className="bg-purple-500 hover:bg-purple-600">
              📊 Aller au Dashboard
            </Button>
            <Button onClick={clearStorage} variant="destructive">
              🗑️ Tout effacer
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            💡 Utilisez "Login → /auth/callback" pour tester le flux normal d'authentification
          </p>
        </div>

        {/* Exchange Result */}
        {exchangeResult && (
          <div className={`rounded-lg p-4 mb-4 ${exchangeResult.error ? 'bg-red-900/50' : 'bg-green-900/50'}`}>
            <h2 className="text-xl font-semibold mb-3">
              {exchangeResult.error ? '❌ Échec Exchange' : '✅ Exchange Réussi'}
            </h2>
            <pre className="bg-gray-900 p-3 rounded text-xs overflow-auto">
              {JSON.stringify(exchangeResult, null, 2)}
            </pre>
          </div>
        )}

        {/* API Test Result */}
        {apiTestResult && (
          <div className={`rounded-lg p-4 mb-4 ${apiTestResult.error || !apiTestResult.ok ? 'bg-red-900/50' : 'bg-green-900/50'}`}>
            <h2 className="text-xl font-semibold mb-3">
              {apiTestResult.ok ? '✅ API Test Réussi' : '❌ API Test Échoué'}
            </h2>
            <pre className="bg-gray-900 p-3 rounded text-xs overflow-auto">
              {JSON.stringify(apiTestResult, null, 2)}
            </pre>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-4">
          <h2 className="text-xl font-semibold mb-3 text-blue-400">📋 Instructions de Test</h2>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>Cliquez sur <strong>"Démarrer Login Google"</strong></li>
            <li>Connectez-vous avec votre compte Google</li>
            <li>Après le retour, observez les <strong>"Parsed Parameters"</strong></li>
            <li>Si session_id est présent, cliquez <strong>"Échanger session_id"</strong></li>
            <li>Puis cliquez <strong>"Tester API /auth/me"</strong> pour vérifier</li>
            <li>Si tout est vert, cliquez <strong>"Aller au Dashboard"</strong></li>
          </ol>
        </div>

        {/* Debug Info Dump */}
        <details className="mt-4">
          <summary className="cursor-pointer text-gray-400 hover:text-white">
            📄 Debug Info Complet (cliquez pour voir)
          </summary>
          <pre className="bg-gray-800 p-4 rounded mt-2 text-xs overflow-auto">
            {JSON.stringify(debugInfo, null, 2)}
          </pre>
        </details>
      </div>
    </div>
  );
};

export default AuthDebug;
